import { useBrain } from "@/contexts/ui/brain";
import { Conversation } from "@/types/conversation/conversation";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface ConversationImproveGenieCTAProps {
  conversation: Conversation;
}

/**
 * Hero CTA in the right rail: takes the full conversation context (summary,
 * topics, key moments, hedge moments, metrics) and kicks the owner into the
 * "Your Genie" assistant with a pre-built improvement prompt. The assistant
 * comes back with concrete recommendations — prompt changes, missing KB
 * articles, goal tweaks — they can apply.
 *
 * This is the loop the user described: every conversation becomes a
 * one-click coaching session for the genie.
 */
export function ConversationImproveGenieCTA({
  conversation,
}: ConversationImproveGenieCTAProps) {
  const { openBrain } = useBrain();

  const handleClick = () => {
    const prompt = buildImprovementPrompt(conversation);
    openBrain(prompt);
  };

  const recommendations = (
    conversation.details?.analysis_result?.recommendations ?? []
  )
    .filter((r): r is string => typeof r === "string" && r.trim().length > 0)
    .slice(0, 4);

  return (
    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-md">
      <div className="rounded-[14px] bg-white overflow-hidden">
        <div
          className="px-5 py-5"
          style={{
            background:
              "linear-gradient(135deg, rgba(78,156,255,0.08) 0%, rgba(255,77,184,0.08) 100%)",
          }}
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="flex-shrink-0">
              <GenieIcon
                name="lightbulb"
                size={28}
                color="#4E9CFF"
                secondaryColor="#FF4DB8"
                gradientEnabled
                label="Improve genie"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold text-gray-900 leading-snug">
                Improve your genie with this call
              </h3>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                Your Genie will read this conversation and recommend prompt,
                knowledge, and goal changes so it handles the next one better.
              </p>
            </div>
          </div>

          {recommendations.length > 0 && (
            <ul className="mb-4 space-y-2">
              {recommendations.map((rec) => (
                <li key={rec} className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]" />
                  <span className="text-[13px] leading-snug text-gray-700">{rec}</span>
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            onClick={handleClick}
            className="w-full inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold text-white shadow-sm hover:shadow-md hover:opacity-95 transition-all"
            style={{
              background: "linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 100%)",
            }}
          >
            Coach this genie
          </button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Prompt builder — pulls every signal we already have on the page into a
// single message Your Genie can reason over. Keep it dense; the assistant
// is better at this than we are.
// ──────────────────────────────────────────────────────────────────────────

function buildImprovementPrompt(conversation: Conversation): string {
  const agentName = conversation.agent?.name || "this genie";
  const agentRole = conversation.agent?.category || conversation.agent?.description || "";
  const details = conversation.details;
  const analysis = details?.analysis_result;

  const summary =
    details?.summary || analysis?.brief_summary || "(no summary available)";
  const topics = Array.isArray(details?.topics) ? details!.topics : [];
  const sentiment = analysis?.sentiment_analysis?.overall_sentiment_score;
  const csat = analysis?.sentiment_analysis?.customer_satisfaction_score;
  const agentTalk = analysis?.talk_metrics?.agent_talk_percentage;
  const customerTalk = analysis?.talk_metrics?.customer_talk_percentage;
  const silence = analysis?.talk_metrics?.silence_percentage;
  const duration = details?.duration;

  const transcript = Array.isArray(details?.transcript) ? details!.transcript : [];
  const topMoments = pickTopMoments(transcript);
  const hedgeMoments = pickHedgeMoments(transcript);

  const sections: string[] = [];

  sections.push(
    `I just reviewed a call with my "${agentName}" genie${
      agentRole ? ` (${agentRole})` : ""
    }. Help me improve the genie so it handles calls like this better next time.`,
  );

  sections.push(`## Summary\n${summary}`);

  if (topics.length > 0) {
    sections.push(`## Topics discussed\n- ${topics.join("\n- ")}`);
  }

  const metricLines: string[] = [];
  if (duration !== undefined && duration !== null) {
    metricLines.push(`- Duration: ${formatDuration(duration)}`);
  }
  if (typeof sentiment === "number") {
    metricLines.push(`- Overall sentiment: ${formatSentiment(sentiment)}`);
  }
  if (typeof csat === "number") {
    metricLines.push(`- Customer satisfaction: ${csat}%`);
  }
  if (typeof agentTalk === "number") {
    metricLines.push(`- Agent talk time: ${agentTalk}%`);
  }
  if (typeof customerTalk === "number") {
    metricLines.push(`- Customer talk time: ${customerTalk}%`);
  }
  if (typeof silence === "number") {
    metricLines.push(`- Silence: ${silence}%`);
  }
  if (metricLines.length > 0) {
    sections.push(`## Metrics\n${metricLines.join("\n")}`);
  }

  if (hedgeMoments.length > 0) {
    const lines = hedgeMoments.map(
      (m) => `- Caller asked: "${m.question}"\n  Genie replied: "${m.reply}"`,
    );
    sections.push(
      `## Moments where the genie hesitated\nThese look like knowledge gaps — places the genie didn't have a confident answer:\n\n${lines.join(
        "\n",
      )}`,
    );
  }

  if (topMoments.length > 0) {
    const lines = topMoments.map(
      (m) => `- [${m.speaker}] "${truncate(m.text, 220)}"`,
    );
    sections.push(`## Key moments from the transcript\n${lines.join("\n")}`);
  }

  sections.push(
    [
      "## What I want from you",
      "Give me concrete, prioritised recommendations to improve this genie based on this call. Cover:",
      "1. **Prompt changes** — what to add/change in the genie's system prompt so it handles similar callers better.",
      "2. **Knowledge base gaps** — specific articles or facts I should upload, with a draft answer where the genie hesitated.",
      "3. **Goal / lead capture tweaks** — fields to add or remove so future calls collect the right info.",
      "4. **Anything else** — tone, structure, handoff, escalation rules.",
      "",
      "Be specific and actionable. Quote from the transcript when it helps. Skip anything that already looks fine.",
    ].join("\n"),
  );

  return sections.join("\n\n");
}

interface Turn {
  speaker: "agent" | "user";
  text: string;
}

interface HedgeMoment {
  question: string;
  reply: string;
}

function normaliseTurn(raw: unknown): Turn | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const text =
    typeof r.text === "string"
      ? r.text
      : typeof r.message === "string"
      ? r.message
      : typeof r.content === "string"
      ? r.content
      : "";
  if (!text || text.length < 4) return null;
  const speakerRaw =
    typeof r.speaker === "string"
      ? r.speaker
      : typeof r.role === "string"
      ? r.role
      : "user";
  const speaker: "agent" | "user" =
    speakerRaw === "agent" || speakerRaw === "assistant" ? "agent" : "user";
  return { speaker, text };
}

const HEDGE_PHRASES = [
  "i don't know",
  "i don't have",
  "i'm not sure",
  "i am not sure",
  "i cannot find",
  "i can't find",
  "i can't help",
  "i'm not able",
  "i don't have access",
  "let me check",
  "i'll have to check",
  "you'll need to",
  "i'm afraid",
  "unfortunately, i",
  "outside my scope",
  "outside my knowledge",
];

function pickHedgeMoments(transcript: unknown[]): HedgeMoment[] {
  const turns = transcript
    .map(normaliseTurn)
    .filter((t): t is Turn => t !== null);

  const out: HedgeMoment[] = [];
  for (let i = 0; i < turns.length; i++) {
    const t = turns[i];
    if (t.speaker !== "user") continue;
    const isQuestion =
      t.text.includes("?") ||
      /^(how|what|when|where|why|who|which|can you|do you|does|is there|are there)\b/i.test(
        t.text.trim(),
      );
    if (!isQuestion) continue;
    const reply = turns.slice(i + 1, i + 3).find((r) => r.speaker === "agent");
    if (!reply) continue;
    const lower = reply.text.toLowerCase();
    if (HEDGE_PHRASES.some((p) => lower.includes(p))) {
      out.push({
        question: t.text.trim(),
        reply: truncate(reply.text.trim(), 200),
      });
    }
  }
  return out.slice(0, 5);
}

function pickTopMoments(transcript: unknown[]): Turn[] {
  const turns = transcript
    .map(normaliseTurn)
    .filter((t): t is Turn => t !== null);

  if (turns.length === 0) return [];

  const scored = turns.map((t) => {
    const lower = t.text.toLowerCase();
    let score = Math.min(t.text.length / 30, 6);
    if (/\$|\bprice|\bquote|\bcost\b|\bpricing\b|\bbudget\b/.test(lower)) score += 4;
    if (/@\w+|\bemail\b|\bphone\b|\bnumber\b|\bcall back\b/.test(lower)) score += 3;
    if (/\bbook\b|\bschedule\b|\bappointment\b|\byes\b|\bdeal\b|\bsign(?:ed)?\b/.test(lower))
      score += 3;
    if (/\?/.test(t.text)) score += 1;
    if (t.speaker === "user") score += 0.5;
    return { turn: t, score };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, 5).map((s) => s.turn);
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max - 1).trimEnd() + "…";
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

function formatSentiment(score: number): string {
  if (score >= -1 && score <= 1) {
    const pct = Math.round(score * 100);
    return `${pct}% (${score < 0.3 ? "low" : score < 0.6 ? "neutral" : "positive"})`;
  }
  return `${Math.round(score)}%`;
}
